import { useEffect, useSyncExternalStore } from 'react';
import { NOTIFICATION_POLL_INTERVAL_MS } from '../constants/notifications';
import { messageThreadApi } from '../services/api';
import type { MessageThreadSummary } from '../types';
import { useRefreshOnNotification } from './useRefreshOnNotification';

/**
 * Unanswered questions and unread threads, for the badge next to Meddelanden
 * (ClickUp 869fegbvk). One shared poll for the whole app; a page that just
 * read or answered a thread calls refreshMessageSummary.
 */
let summary: MessageThreadSummary | null = null;
const listeners = new Set<() => void>();
let users = 0;
let timer: ReturnType<typeof setInterval> | null = null;

const emit = (next: MessageThreadSummary | null) => {
  summary = next;
  listeners.forEach(listener => listener());
};

export async function refreshMessageSummary() {
  try {
    emit(await messageThreadApi.summary());
  } catch {
    // Keep the last count; the next poll tries again.
  }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/** Only the parties of a project have conversations; others never poll. */
export function useMessageSummary(enabled: boolean): MessageThreadSummary | null {
  useEffect(() => {
    if (!enabled) return;
    users += 1;
    if (users === 1) {
      void refreshMessageSummary();
      timer = setInterval(() => { void refreshMessageSummary(); }, NOTIFICATION_POLL_INTERVAL_MS);
    }
    return () => {
      users -= 1;
      if (users === 0 && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
  }, [enabled]);
  useRefreshOnNotification(() => (enabled ? refreshMessageSummary() : undefined));
  const value = useSyncExternalStore(subscribe, () => summary);
  return enabled ? value : null;
}

/** For tests. */
export function resetMessageSummary() {
  emit(null);
}
